import { ClassificationsService } from '@/domain/classifications/classifications.service';
import type { InfiniteHandler } from '@/shared/utils/angular-sdk';
import { UiButtonComponent } from '@/ui/button/ui-button';
import { UiFieldComponent } from '@/ui/field/ui-field';
import { UiIconComponent } from '@/ui/icon/ui-icon';
import { debouncedSignal } from '@/utils/angular/signals/debounced-signal';
import { debounceTimeAllButFirst } from '@/utils/rxjs/operators/debounce-time-all-but-first.operator';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, type ElementRef, EventEmitter, inject, input, type OnInit, Output, signal, ViewChild } from '@angular/core';
import { rxResource, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import { filter, firstValueFrom } from 'rxjs';
import { map, skip } from 'rxjs/operators';
import { AdvizeoInputDirective } from '../../directives/advizeo-input/advizeo-input.directive';
import { EllipsisWithTitleFallbackDirective } from '../../directives/ellipsis-with-title-fallback/ellipsis-with-title-fallback.directive';
import type { BuildingId } from '../../interfaces/Building.interface';
import { ClassificationNodeId, ClassificationNodeType } from '../../interfaces/Classification.interface';
import { SharedUiInfiniteScrollingComponent } from '../../libs/shared/ui/infinite-scrolling/lib/shared-ui-infinite-scrolling.component';
import { ClassificationNodeNamePipe } from '../../pipes/classification-node-name.pipe';
import { LocalNumberPipe } from '../../pipes/localnumber.pipe';
import { BuildingsProvider } from '../../providers/buildings/buildings.provider';
import {
    type ClassificationNodeBuildingDetails,
    type ClassificationNodeV2WithBreadcrumb,
    ClassificationsProviderV2
} from '../../providers/classification/classifications-v2.provider';
import { AdvizeoModalService } from '../../services/advizeo-modal.service';
import { type ClassificationNodesColumn, ClassificationNodeSelectorV2Service } from './classification-node-selector-v2.service';
import { FavoritePerimeterModalComponent } from './modal/favorite-perimeter-modal';
import type { ClassificationNodeLight } from './modal/favorite-perimeter.interface';
import { FavoritePerimeterService } from './modal/service/favorite-perimeter.service';

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./classification-node-selector-v2.scss'],
    selector: 'classification-node-selector-v2',
    templateUrl: 'classification-node-selector-v2.html',
    imports: [
        UiFieldComponent,
        AdvizeoInputDirective,
        FormsModule,
        SharedUiInfiniteScrollingComponent,
        UiIconComponent,
        EllipsisWithTitleFallbackDirective,
        ClassificationNodeNamePipe,
        TranslatePipe,
        AsyncPipe,
        LocalNumberPipe,
        UiButtonComponent
    ]
})
export class ClassificationNodeSelectorV2Component implements OnInit {
    private _classificationsProvider = inject(ClassificationsProviderV2);
    private _buildingsProvider = inject(BuildingsProvider);
    private _favoritePerimeterService = inject(FavoritePerimeterService);
    private _modalService = inject(AdvizeoModalService);
    private _classificationsService = inject(ClassificationsService);
    private _classificationNodeSelectorV2Service = inject(ClassificationNodeSelectorV2Service);

    classificationNodeId = input<ClassificationNodeId | null>(null);
    enableBuildingSelection = input<boolean>(false);
    enableAllBuildingsNodeSelection = input<boolean>(false);
    keepNavigation = input<boolean>(true);

    @Output() selectedClassificationNodeLight: EventEmitter<ClassificationNodeLight> = new EventEmitter();
    @Output() selectedFavoriteClassificationNodeId: EventEmitter<ClassificationNodeId> = new EventEmitter();

    rootColumn: ClassificationNodesColumn = {
        openedNode: null,
        nodes: [],
        isLoading: true,
        isError: false
    };

    isPickingNode = this.keepNavigation() ? this._classificationNodeSelectorV2Service.storedIsPickingNode : signal(false);

    pickedClassificationNodeId = signal<ClassificationNodeId | null>(null);

    selectedClassificationNodeGroup = this.keepNavigation()
        ? this._classificationNodeSelectorV2Service.storedClassificationNodeSelected
        : signal<ClassificationNodeId>(ClassificationNodeId.ALL_BUILDINGS_NODE);

    classificationNodeColumns = this.keepNavigation()
        ? this._classificationNodeSelectorV2Service.storedClassificationNodeColumns
        : signal<ClassificationNodesColumn[]>([this.rootColumn]);

    openedClassificationNodeSearch = this.keepNavigation()
        ? this._classificationNodeSelectorV2Service.storedOpenedClassificationNodeSearch
        : signal<ClassificationNodeId | null>(null);

    searchText = this.keepNavigation() ? this._classificationNodeSelectorV2Service.storedSearch : signal<string>('');
    searchText$ = toObservable(this.searchText);

    buildingsHandler!: InfiniteHandler<ClassificationNodeBuildingDetails, { classificationNodeId: ClassificationNodeId }>;
    searchedClassificationNodes!: InfiniteHandler<ClassificationNodeV2WithBreadcrumb, { search: string }>;
    searchedBuildings!: InfiniteHandler<ClassificationNodeBuildingDetails, { search: string }>;

    debouncedSearch = debouncedSignal(this.searchText, 500);
    isSearching = computed(() => this.debouncedSearch() !== '');
    isSearchingBuilding = computed(() => this.isSearching() && !this.isPickingNode());

    ALL_BUILDINGS_NODE = ClassificationNodeId.ALL_BUILDINGS_NODE;

    buildingsCount$ = this._buildingsProvider.getBuildingsCount$();
    favoritePerimeterCount = rxResource({
        defaultValue: 0,
        stream: () => this._favoritePerimeterService.getFavoriteActivities$().pipe(map(activities => activities.length))
    });

    @ViewChild('classificationsHorizontalScroll', { static: false }) classificationsHorizontalScroll!: ElementRef<HTMLDivElement>;

    ngOnInit() {
        if (this.classificationNodeId()) {
            this.pickedClassificationNodeId.set(this.classificationNodeId());
        }
        firstValueFrom(this._classificationsService.getRootClassificationNodes$().pipe(filter(isNotNullish))).then(rootNodes => {
            this.rootColumn.nodes = rootNodes;
            this.rootColumn.isLoading = false;

            if (this.classificationNodeColumns().length === 0 && this.searchText() === '') {
                this.classificationNodeColumns.set([this.rootColumn]);
            }
        });
        this.buildingsHandler = this._classificationsProvider.getClassificationNodeBuildings().handleInfinite(30);
        this.buildingsHandler.setParams({
            classificationNodeId: this.isSearchingBuilding()
                ? ClassificationNodeId.ALL_BUILDINGS_NODE
                : this.selectedClassificationNodeGroup()
        });
        this.buildingsHandler.contentReset$.pipe(untilDestroyed(this)).subscribe(this.scrollBuildingToTop);

        this.searchedBuildings = this._classificationsProvider.getBuildingsSearched().handleInfinite(30);
        this.searchedBuildings.contentReset$.pipe(untilDestroyed(this)).subscribe(this.scrollSearchedBuildingToTop);

        this.searchedClassificationNodes = this._classificationsProvider.getClassificationNodesSearched().handleInfinite(30);
        this.searchedClassificationNodes.contentReset$.pipe(untilDestroyed(this)).subscribe(this.scrollSearchedClassificationToTop);

        this.searchText$.pipe(debounceTimeAllButFirst(500), untilDestroyed(this)).subscribe(search => {
            if (this.enableBuildingSelection()) {
                this.searchedBuildings.setParams({ search });
            }
            this.searchedClassificationNodes.setParams({ search });
        });

        this.searchText$.pipe(skip(1), untilDestroyed(this)).subscribe(search => {
            this.openedClassificationNodeSearch.set(null);
            this.classificationNodeColumns.set(search !== '' ? [] : [this.rootColumn]);
            this.isPickingNode.set(false);
        });

        setTimeout(() => this.scrollClassificationToRight(), 50);
    }

    selectTopLevelClassificationNode() {
        this.selectClassificationNode({
            classificationId: null,
            classificationNodeId: ClassificationNodeId.ALL_BUILDINGS_NODE,
            classificationNodeType: ClassificationNodeType.CLASSIFICATION_GROUP
        });
    }

    selectClassificationNode(classificationNodeLight: ClassificationNodeLight) {
        this.pickedClassificationNodeId.set(classificationNodeLight.classificationNodeId);
        this.selectedClassificationNodeGroup.set(classificationNodeLight.classificationNodeId);
        this.isPickingNode.set(true);
        this.buildingsHandler.setParams({
            classificationNodeId: classificationNodeLight.classificationNodeId
        });
        this.selectedClassificationNodeLight.emit(classificationNodeLight);
    }

    selectBuilding(buildingId: BuildingId) {
        this.pickedClassificationNodeId.set(buildingId as ClassificationNodeId);
        this.selectedClassificationNodeLight.emit({
            classificationId: null,
            classificationNodeId: buildingId as ClassificationNodeId,
            classificationNodeType: ClassificationNodeType.BUILDING
        });
    }

    openFavoriteParameterSelectorModal() {
        this._modalService
            .openAdvizeoModal(FavoritePerimeterModalComponent, {})
            .afterClosed()
            .then((selectedFavoritePerimeter: ClassificationNodeId | undefined) => {
                if (isNotNullish(selectedFavoritePerimeter)) {
                    this.selectedFavoriteClassificationNodeId.emit(selectedFavoritePerimeter);
                }
            });
    }

    toggleClassificationNode(classificationNodeId: ClassificationNodeId, columnIndex: number) {
        if (classificationNodeId === this.classificationNodeColumns()[columnIndex].openedNode) {
            this.classificationNodeColumns.set(this.classificationNodeColumns().slice(0, columnIndex + 1));
            this.classificationNodeColumns()[columnIndex].openedNode = null;
        } else {
            this.classificationNodeColumns.set(this.classificationNodeColumns().slice(0, columnIndex + 1));
            this.classificationNodeColumns()[columnIndex].openedNode = classificationNodeId;

            const newColumn: ClassificationNodesColumn = { openedNode: null, nodes: [], isLoading: true, isError: false };
            this.classificationNodeColumns().push(newColumn);
            setTimeout(() => {
                this.scrollClassificationToRight();
            }, 50);

            firstValueFrom(this._classificationsProvider.getClassificationNodeGroupChildren$(classificationNodeId))
                .then(nodes => {
                    newColumn.nodes = nodes;
                    newColumn.isLoading = false;
                })
                .catch(() => {
                    newColumn.isError = true;
                    newColumn.isLoading = false;
                });
        }
    }

    toggleClassificationNodeSearch(classificationNodeId: ClassificationNodeId) {
        this.classificationNodeColumns.set([]);

        if (classificationNodeId === this.openedClassificationNodeSearch()) {
            this.openedClassificationNodeSearch.set(null);
        } else {
            this.openedClassificationNodeSearch.set(classificationNodeId);

            const newColumn: ClassificationNodesColumn = { openedNode: null, nodes: [], isLoading: true, isError: false };
            this.classificationNodeColumns().push(newColumn);

            setTimeout(() => {
                this.scrollClassificationToRight();
            }, 50);

            firstValueFrom(this._classificationsProvider.getClassificationNodeGroupChildren$(classificationNodeId)).then(nodes => {
                newColumn.nodes = nodes;
                newColumn.isLoading = false;
            });
        }
    }

    scrollClassificationToRight() {
        this.classificationsHorizontalScroll.nativeElement.scrollTo({ left: 5000, behavior: 'smooth' });
    }

    scrollBuildingToTop() {
        const element = document.getElementById('scrollBuildingContent');
        if (element !== null && element !== undefined) {
            element.scrollTop = 0;
        }
    }
    scrollSearchedClassificationToTop() {
        const element = document.getElementById('scrollSearchedClassificationContent');
        if (element !== null && element !== undefined) {
            element.scrollTop = 0;
        }
    }

    scrollSearchedBuildingToTop() {
        const element = document.getElementById('scrollSearchedBuildingContent');
        if (element !== null && element !== undefined) {
            element.scrollTop = 0;
        }
    }
}
